
// ── Result<V, E> — the one value-typed success/failure channel ─────────────────
//
// A single homegrown discriminated union replaces the reducers'
// throw-based failure channel and the ad-hoc `{ ok, error }` shapes. A reducer (or
// any operation with an *expected* rejection) returns `err(...)` instead of
// throwing; a caller can't reach `.value` without first narrowing on `ok`, so
// "forgot to handle the rejection" becomes a tsc error rather than a runtime
// ws-handler crash. Throw stays for genuine programmer errors only — those still
// propagate, and the boundary catches keep re-raising non-`Result` throws.
//
// No library: a 3-line union has no API to misremember
// and `if (r.ok)` is the single most-trained TS narrowing pattern. The cost is no
// `.andThen` sugar — explicit early-returns instead — which is the cost agents
// tolerate best. Flip to neverthrow *deliberately* (and at full depth) if a future
// maintainer re-weights toward human ergonomics; never silently add the dep.

/**
 * A success-or-failure value. `ok: true` carries the produced `value`; `ok: false`
 * carries the `error` (defaulting to a `string` message). Narrow on `r.ok` to reach
 * either arm — there is no way to touch `.value` without it, which is the point.
 */
export type Result<V, E = string> =
  | { ok: true; value: V }
  | { ok: false; error: E };

/** Construct a success `Result` carrying `value`. */
export function ok<V>(value: V): Result<V, never> {
  return { ok: true, value };
}

/** Construct a failure `Result` carrying `error`. */
export function err<E>(error: E): Result<never, E> {
  return { ok: false, error };
}

/**
 * Unwrap a `Result` to its value, **throwing** the error on `!ok`. For the narrow
 * case where a rejection would be a genuine bug, not an expected outcome — a
 * *trusted* apply (a resolution the authority itself just minted, a replay of an
 * already-validated log) and test setup that wants the value or a hard failure.
 * Production paths that handle an *expected* rejection must branch on `!ok`
 * instead; this is the escape hatch for "this can't fail here, and if it does I
 * want the throw." An `Error` error is rethrown as-is; anything else is wrapped.
 */
export function unwrap<V, E>(result: Result<V, E>): V {
  if (result.ok) return result.value;
  throw result.error instanceof Error ? result.error : new Error(String(result.error));
}

/**
 * The message out of an unknown thrown value: `.message` when it is an `Error`, `String(it)`
 * otherwise. **The single most-repeated expression in the codebase** — 31 non-test copies of
 * `thrown instanceof Error ? thrown.message : String(thrown)` across `cli/`, `backend/`,
 * `frontend/`, `packs/` and this very file, found by the try/catch audit's Phase 3.
 *
 * It lives beside {@link parseJson}/{@link parseUrl} because it is the same species of thing:
 * the one-line adapter between JS's untyped throw channel and a `string` an error path can
 * actually carry. It is **not** a `try` hoist and removes no blocks — a `catch` still has to
 * exist to hand it a value. What it removes is the chance of writing the ternary a 32nd time
 * with the arms swapped, or reaching for a bare `String(err)` that renders `"Error: …"` where
 * the caller wanted the bare message.
 *
 * Deliberately narrow: it does not touch `.stack`, `.cause`, or `AggregateError` members. A
 * site that needs those is a structured loud-failure (see `CODE_STYLE.md`'s throw paragraph)
 * and should keep the thrown value, not flatten it here.
 */
export function errorMessage(thrown: unknown): string {
  return thrown instanceof Error ? thrown.message : String(thrown);
}

/**
 * `JSON.parse` on the `Result` channel. Malformed input is an *expected* rejection at
 * every boundary we read JSON from — a hand-edited `localStorage` value, a truncated
 * file, a body that isn't the JSON its content-type promised — so it belongs in the
 * signature, not in a `throw` each caller has to remember to wrap.
 *
 * This is the one place the stdlib's throwing parse is caught. A call site that wants
 * "fall back to a default on garbage" writes `const r = parseJson<T>(raw); if (!r.ok)
 * return fallback;` — no `try` in sight. **The parse is all this validates**: `T` is an
 * unchecked assertion, exactly as `JSON.parse(...) as T` was, so a caller that cares
 * about the shape still narrows on the value.
 */
export function parseJson<T>(raw: string): Result<T> {
  try {
    return ok(JSON.parse(raw) as T);
  } catch (thrown) {
    return err(errorMessage(thrown));
  }
}

/**
 * `new URL(...)` on the `Result` channel — the second throwing stdlib API this repo wraps once.
 *
 * A server base that isn't a parseable URL is an *expected* rejection wherever we normalize one:
 * a `--server` flag, `LVTT_SERVER`/`LVTT_URL`, a `baseUrl` a test passes in, a browser `Origin`
 * header, a stack frame's script URL. **Seven** call sites hand-rolled the identical guard around
 * it and now narrow on `!ok` instead: `cli/src/roster-session.ts`, `cli/src/wsRoundTrip.ts`,
 * `cli/src/gameLogin.ts`, `cli/src/game-session.ts`, `frontend/src/lib/game/restLogin.ts`,
 * `backend/src/boot.ts`, `backend/src/clientErrors/symbolicate.ts`.
 *
 * **Most callers only need "did it parse", and that is fine** — five of the seven fall back to the
 * raw input and let the `WebSocket`/`fetch` constructor surface the real error, so they never read
 * the message; the other two (`boot.ts` rejects the origin, `symbolicate.ts` falls back to a bare
 * path) branch on the failure without reading it either. It carries a message anyway, to match
 * {@link parseJson}: these two are the same species of wrapper on the same shelf, and giving them
 * different shapes would just be a decision the next author has to make.
 *
 * Like `parseJson`, this validates only the *parse*. A URL that parses is not thereby a URL you
 * should connect to — scheme, host and path are the caller's business (`wsRoundTrip.ts`'s
 * `normalizeSchemelessUrl` supplies a missing scheme, `resolveServerUrl` strips the path,
 * `httpOriginOf` forces the scheme).
 */
export function parseUrl(raw: string, base?: string): Result<URL> {
  try {
    return ok(new URL(raw, base));
  } catch (thrown) {
    return err(errorMessage(thrown));
  }
}

/**
 * The **HTTP(S) origin** a ws (or http) server URL's REST endpoints live on: force the scheme to
 * `https:` for a secure input (`wss:`/`https:`) and `http:` otherwise, then strip path, query and
 * fragment so the caller owns the suffix it appends. An input that doesn't parse comes back
 * **unchanged**, so a malformed `--server` still reaches `fetch` and surfaces its own error.
 *
 * The ws drivers carry a ws base (`ws://host:3001`, often with a `/ws/...` path) while the login
 * endpoint is plain http(s) on the same host, so both clients needed this and both wrote it:
 * `cli/src/gameLogin.ts`'s `httpOriginFromWs` and `frontend/src/lib/game/restLogin.ts`'s
 * `httpOrigin` were byte-identical apart from the frontend's `"" ⇒ ""` same-origin guard, which
 * stays at that call site because it is a *frontend* fact (production serves the SPA from the
 * backend's own origin, so the relative path is correct there and meaningless in the CLI).
 *
 * It lives here rather than in either client because `shared/` is the only package both `cli/`
 * and `frontend/` can import. `CLAUDE.md`'s "the wire protocol lives in `shared/` only" governs
 * where protocol *messages* are declared, not which utilities may sit beside them — this module
 * already holds `ok`/`err`/`unwrap`/`errorMessage`/`parseJson`/`parseUrl`.
 */
export function httpOriginOf(url: string): string {
  const parsed = parseUrl(url);
  if (!parsed.ok) return url;
  const u = parsed.value;
  u.protocol = u.protocol === "wss:" || u.protocol === "https:" ? "https:" : "http:";
  u.pathname = "";
  u.search = "";
  u.hash = "";
  return u.toString().replace(/\/$/, "");
}
